import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, useContext } from "react";
import { AuthContext } from "utils/AuthContext";
import { Flex, FlexLeftCol, FlexBottom, HScroll } from "styles/global-style";
import useTimelineScroll from "utils/useTimelineScroll";
import CircleArrow from "components/CircleArrow";
import CirclePlus from "components/CirclePlus";
import EdgeArrow from "components/Timeline/EdgeArrow";
import DaySelect from "components/Timeline/DaySelect";
import DatesGoals from "components/Timeline/DatesGoals";
import Row from "components/Timeline/Row";
import Dot from "components/Timeline/Dot";
import TodayButton from "components/Timeline/TodayButton";
import GoalPopup from "components/Timeline/GoalPopup";
import AddPersonPopup from "components/Timeline/AddPersonPopup";
import InviteUserPopup from "components/Timeline/InviteUserPopup";
import WelcomePopup from "components/Trial/WelcomePopup";
import TrialNudge from "components/Trial/TrialNudge";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import useKey from "utils/KeyListener";
import FilterButton from "components/FilterButton";
import { FaTimes } from "react-icons/fa";
import { getDefaultProjectData } from "utils/DefaultProjectData";

import arrow from "imgs/arrow.png";

const clone = require("rfdc")();

const GRID_SPACE_Y = 2;
const GRID_SPACE_X = 2;
const GRID_SPACE_WEEKEND = 6;
const BOX_SIZE = 16;
const FONT_WIDTH = 7;
const NAME_COLUMN_GAP = 0; // space between longest name and ellipsis
const ELLIPSIS_BUTTON_WIDTH = 16;
const MAX_DISPLAY_NAME_CHARS = 24;
// Minimum character count for the name column. Ensures the column is wide
// enough for the scroll arrows and the "YYYY Month" label that sit above it
// to never overlap the name plates (even when names are short like "You").
const MIN_DISPLAY_NAME_CHARS = 14;
// Minimum height of the rows area (dots grid + name column). Keeps an
// invisible scroll catcher under the rows so horizontal scroll gestures keep
// working over the grid even when few (or no) rows are visible.
const MIN_ROWS_AREA_HEIGHT = 80;

// Date universe spans a bounded range around today and is extended dynamically
// at the edges as the user scrolls (see handleNearEdge / useTimelineScroll).
const UNIVERSE_PAST_DAYS = 180;
const UNIVERSE_FUTURE_DAYS = 180;
const EXTEND_CHUNK_DAYS = 90;
// Where "today" sits horizontally on initial load / Today button.
// Days view: exactly three weeks of history to the left of today (the slot
// three weeks back sits flush against the viewport's left edge).
// Weeks view: today a quarter in from the left edge.
const TODAY_HISTORY_DAYS = 21;
const TODAY_ALIGN = 0.25;

export default function Timeline() {
  const { user, supabase, roleAccess, refresh, isTrial } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [people, setTimeline] = useState(); // List of all people in the org
  const [showDaySelect, setShowDaySelect] = useState(false);
  const [showEditGoal, setShowEditGoal] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [showTrialNudge, setShowTrialNudge] = useState(false);
  const [todayInRange, setTodayInRange] = useState(true);
  const [windowRange, setWindowRange] = useState({ start: 0, end: 0 }); // visible slot index window (+buffer)
  const [viewportRange, setViewportRange] = useState({ start: 0, end: 0 }); // strictly visible slot index range (no buffer)
  const [hideDeparted] = useState(!user.proj.settings.find((s) => s.name === "departedHidden"));
  const [hasProjectAccess, setHasProjectAccess] = useState(true);
  const [hideGottaGoal, setHideGottaGoal] = useState(false);
  const [viewMode, setViewMode] = useState('days'); // 'days' or 'weeks'
  const [groupBy, setGroupBy] = useState('noGroup'); // 'noGroup' or 'discipline'
  const [showPrivateGoals, setShowPrivateGoals] = useState(false);
  const [showFullTeam, setShowFullTeam] = useState(false);
  const [disciplines, setDisciplines] = useState([]);
  const [hoveredPersonId, setHoveredPersonId] = useState(null);
  const [longestNameCharCount, setLongestNameCharCount] = useState(MIN_DISPLAY_NAME_CHARS); // state so name column re-renders when data loads
  const [gridHeight, setGridHeight] = useState(0); // measured height of the day-dots grid (for the edge scroll overlays)
  const userChangingViewMode = useRef(false);
  const userChangingGroupBy = useRef(false);
  const userChangingPrivateGoals = useRef(false);
  const userChangingTeamFilter = useRef(false);

  const containerRef = useRef(); //Grid container div
  const scrollerRef = useRef(); // horizontally scrollable viewport around the grid
  // State-backed handle to the scroller DOM node so scroll/wheel listeners
  // (useTimelineScroll) re-attach if the node is unmounted and remounted
  // (e.g. a transient "no access" render during first-login hydration).
  const [scrollerEl, setScrollerEl] = useState(null);
  const attachScrollerRef = useCallback((el) => {
    scrollerRef.current = el;
    setScrollerEl(el);
  }, []);
  const peopleAllRawData = useRef(); //DB fetch storage
  const peopleAllFormatted = useRef(); //DB fetch storage but formatted
  const goals = useRef(); //DB fetch storage
  const longestName = useRef(MIN_DISPLAY_NAME_CHARS); // ref for buildGrid (sync'd when we have data)
  const startDate = useRef(new Date().subtractDays(10, true)); // earliest date in the universe (left edge)
  const universeStart = useRef(new Date().subtractDays(UNIVERSE_PAST_DAYS, true)); // universe range start
  const universeEnd = useRef(new Date().addDays(UNIVERSE_FUTURE_DAYS, true)); // universe range end
  const pendingAnchor = useRef(null); // { kind, date, offsetFromLeft, mode } to restore scroll after a rebuild
  const daysAnchor = useRef(null); // exact days-view anchor when leaving days mode, for lossless weeks->days round trips
  const didInitialScroll = useRef(false); // whether we've centered on today after first load
  const initializedScrollerEl = useRef(null); // scroller node the initial alignment was applied to
  const extendingRef = useRef(false); // guard so edge extension fires once per rebuild
  const dates = useRef();
  const selectedDays = useRef();
  const selectionAnchorRef = useRef(null); // { row, col } for shift+click range (row = visual index in peopleForDots)
  const peopleForDotsRef = useRef([]); // current visual row order (set during render) so submit/leave resolve row index
  const personYOffsetsRef = useRef({}); // current visual row Y offsets (for mouse-position based row highlight)
  const editingGoal = useRef();
  const newlyCreatedGoalIdRef = useRef(null); // id of goal just created via Add goal; if closed with no title/body we delete it
  const invitingUser = useRef();
  const movingDay = useRef();
  const showWeekends = useRef();
  const tl = useRef();
  const addPersonDate = useRef();
  const dotsContainerRef = useRef();
  const dotsGridRef = useRef(); // inner grid div containing only the row dots (used for row-Y bounds)
  const rowsAreaRef = useRef(); // wrapper containing names column + dots grid (used for horizontal bounds)
  const hoveredPersonIdRef = useRef(null);
  const animationFrameId = useRef();
  const rowHoverAnimationFrameId = useRef();
  const xIndexRef = useRef();
  const targetMovedJoinLeaveDay = useRef();

  // Keep gridHeight in sync with the day-dots grid so the edge scroll
  // overlays cover exactly the dots area (not the dates/goals strip above).
  useEffect(() => {
    const el = dotsGridRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setGridHeight(el.offsetHeight));
    observer.observe(el);
    setGridHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, [loading]);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  useKey("ArrowRight", false, () => {
    scrollToDate(getCenterDate().addDays(30, true));
  });

  useKey("ArrowLeft", false, () => {
    scrollToDate(getCenterDate().subtractDays(30, true));
  });

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  const buildGrid = useCallback(
    () => {
      // The grid is no longer bound to the viewport width. We generate a stable
      // geometry "universe" spanning universeStart..universeEnd with absolute,
      // scroll-independent left/right positions; rendering is windowed elsewhere.
      startDate.current = universeStart.current;

      let date = new Date(universeStart.current);
      const rangeEnd = universeEnd.current;
      let gridPositionsX = [];
      let x = 0;

      if (viewMode === "weeks") {
        // Week-based grid generation
        while (date <= rangeEnd) {
          // Find the start of the week (Monday)
          const weekStart = new Date(date);
          const dayOfWeek = weekStart.getDay();
          const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, Monday = 1
          weekStart.setDate(weekStart.getDate() - daysToMonday);
          
          // Find the end of the week (Sunday)
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          
          const slot = { 
            left: x, 
            right: x + (BOX_SIZE * 2) + GRID_SPACE_X, // Two dots wide
            date: weekStart,
            weekEnd: weekEnd,
            isWeekMode: true
          };
          gridPositionsX.push(slot);
          x += (BOX_SIZE * 2) + GRID_SPACE_X + GRID_SPACE_X; // Extra spacing for week mode
          date = new Date(weekStart);
          date.setDate(date.getDate() + 7); // Move to next week
        }
      } else {
        // Original day-based grid generation
        while (date <= rangeEnd) {
          const slot = { left: x, right: x + BOX_SIZE, date: date, isWeekMode: false };
          gridPositionsX.push(slot);
          x += BOX_SIZE + GRID_SPACE_X;
          date = date.addDays(1);
          if (showWeekends.current) {
            if ([1, 6].includes(date.getDay())) x += GRID_SPACE_WEEKEND; // Add extra space for weekends
          } else if (date.getDay() === 6) {
            x += GRID_SPACE_WEEKEND;
            date = date.addDays(2); // Skip to Monday if weekends are not shown
          }
        }
      }

      let _people = [];

      // Process each person and their available days
      for (let i = 0; i < peopleAllRawData.current.length; i++) {
        const person = peopleAllRawData.current[i];

        // Extract and sort join and leave days
        const joinLeaveDays = person.days
          .filter((day) => day.system_type === "joins" || (day.system_type === "leaves" && day.project_id === user.proj.id))
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        let _activeRanges = []; // Ranges of dates where person is active on project
        let currentJoinDate = null;

        // Create ranges of active dates based on joins and leaves
        joinLeaveDays.forEach((day) => {
          if (day.system_type === "joins") {
            currentJoinDate = new Date(day.date);
          } else if (day.system_type === "leaves" && currentJoinDate) {
            _activeRanges.push([currentJoinDate, new Date(day.date).addDays(1, true)]);
            currentJoinDate = null;
          }
        });

        // If there's a trailing joins date without a leaves date, consider the range as perpetually active
        if (currentJoinDate) {
          _activeRanges.push([currentJoinDate, null]); // null indicates perpetual activity
        }

        _people[i] = {
          person: person,
          days: [],
          activeRanges: _activeRanges,
        };

        // Map over the grid positions to add days for each person
        let prevActive = false;
        for (let j = 0; j < gridPositionsX.length; j++) {
          const currentSlot = gridPositionsX[j];
          let isActive = false; // Declare isActive at this scope
          
          if (viewMode === "weeks") {
            // Week mode: check if any day in the week is active
            const weekStart = currentSlot.date;
            const weekEnd = currentSlot.weekEnd;
            isActive = _activeRanges.some(
              ([joinDate, leaveDate]) => {
                const joinInWeek = joinDate <= weekEnd && (leaveDate === null || leaveDate >= weekStart);
                const leaveInWeek = leaveDate !== null && leaveDate >= weekStart && leaveDate <= weekEnd;
                return joinInWeek || leaveInWeek;
              }
            );

            // Find the most common day status for the week
            let weekDayStatus = null;
            let workingDays = 0;
            let totalDays = 0;
            
            for (let d = new Date(weekStart); d <= weekEnd; d.setDate(d.getDate() + 1)) {
              const dayStatus = person.days.find((storedDay) => storedDay.date === d.toISOString().split("T")[0]);
              if (dayStatus) {
                totalDays++;
                if (dayStatus.day_type?.style === "working") {
                  workingDays++;
                }
                if (!weekDayStatus) weekDayStatus = dayStatus;
              }
            }

            // Determine week status based on majority
            if (workingDays > totalDays / 2) {
              weekDayStatus = { day_type: { style: "working" } };
            } else if (weekDayStatus) {
              weekDayStatus = { day_type: { style: weekDayStatus.day_type?.style || "working" } };
            }

             _people[i].days.push({
               pos: currentSlot,
               day: weekStart,
               weekEnd: weekEnd,
               day_type: weekDayStatus?.day_type,
               system_type: weekDayStatus?.system_type,
               daystamp: weekStart.daystamp(),
               id: weekDayStatus?.id,
               style: weekDayStatus?.day_type?.style || "working",
               personIndex: i,
               dayIndex: j,
               isActive,
               isFirstOrLast: prevActive !== isActive,
               isWeekMode: true,
               workingDays,
               totalDays
             });
          } else {
            // Original day mode logic
            const currentDay = currentSlot.date;
            isActive = _activeRanges.some(
              ([joinDate, leaveDate]) => currentDay >= joinDate && (leaveDate === null || currentDay <= leaveDate) // null leaveDate means perpetual
            );

            // Find the status of the current day (working, etc.). Prefer a day with day_type set when multiple rows exist (e.g. join/leave + type).
            const dateStr = currentDay.toISOString().split("T")[0];
            const sameDateDays = person.days.filter((d) => d.date === dateStr);
            const dayStatus = sameDateDays.find((d) => d.day_type) ?? sameDateDays[0];

            _people[i].days.push({
              pos: currentSlot,
              day: currentDay,
              day_type: dayStatus?.day_type,
              system_type: dayStatus?.system_type,
              daystamp: currentDay.daystamp(),
              id: dayStatus?.id,
              style: dayStatus?.day_type?.style || "working",
              personIndex: i,
              dayIndex: j,
              isActive, // Determine if the day should be rendered
              isFirstOrLast: prevActive !== isActive, // Is a joins or leaves date, enabling user to move date
              isWeekMode: false
            });
          }

          // Since active is looking backwards, on a leaves day we need to go back a step to fix
          if (prevActive && !isActive) {
            _people[i].days[j - 1].isFirstOrLast = true;
          }

          prevActive = isActive;
        }
      }

      // todayInRange now reflects whether today is inside the visible window
      // (computed in a dedicated effect), not whether it is in the universe.
      dates.current = gridPositionsX;
      peopleAllFormatted.current = _people;

      // APPLY ANY DISPLAY FILTER to people list xxxxxxxxxxxxxxxxxxxxxxxxxxx
      let _peopleFiltered = clone(_people);
      _peopleFiltered = _peopleFiltered.filter((member) => {
        if (member.activeRanges.length === 0) return false;
        const lastActiveRange = member.activeRanges.slice(-1);
        const neverLeaves = lastActiveRange[0][1] === null;
        return neverLeaves || lastActiveRange[0][1] > startDate.current || !hideDeparted;
      });

      // // Order on startdate, earliest on top
      // _peopleFiltered = _peopleFiltered.sort((a, b) => new Date(a.activeRanges[0]) - new Date(b.activeRanges[0]));

      // After sorting, update personIndex for each day to reflect the new order
      for (let i = 0; i < _peopleFiltered.length; i++) {
        const days = _peopleFiltered[i].days;
        for (let j = 0; j < days.length; j++) {
          days[j].personIndex = i;
        }
      }

      setTimeline(_peopleFiltered);
    },
    [peopleAllRawData, setTimeline, dates, user.proj.id, hideDeparted, viewMode]
  );

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
  // Horizontal virtual scroll: geometry helpers + windowing + edge extension

  // Index of the last slot whose date is on or before `date` (works for
  // day/week/month slot starts).
  const findSlotIndexByDate = useCallback((date) => {
    const slots = dates.current;
    if (!slots?.length) return 0;
    const iso = date.getISODate();
    let ans = 0;
    for (let i = 0; i < slots.length; i++) {
      if (slots[i].date.getISODate() <= iso) ans = i;
      else break;
    }
    return ans;
  }, [dates]);

  // The date currently at the horizontal center of the viewport.
  const getCenterDate = useCallback(() => {
    const el = scrollerRef.current;
    const slots = dates.current;
    if (!el || !slots?.length) return new Date();
    const centerPx = el.scrollLeft + el.clientWidth / 2;
    let idx = 0;
    for (let i = 0; i < slots.length; i++) {
      if (slots[i].left <= centerPx) idx = i;
      else break;
    }
    return new Date(slots[idx].date);
  }, [dates]);

  // Anchor the view on a stable visual reference before a geometry rebuild
  // (prepend extension, viewMode change): today's slot when it is in view,
  // otherwise the first visible month label. Restoring puts that reference at
  // the same pixel offset from the viewport's left edge, so a viewMode switch
  // reads as the grid compressing/expanding around that fixed point. Falls
  // back to the slot under the viewport center when neither is available.
  const captureAnchor = useCallback(() => {
    const el = scrollerRef.current;
    const slots = dates.current;
    if (!el || !slots?.length) return;
    const viewLeft = el.scrollLeft;
    const viewRight = viewLeft + el.clientWidth;
    const mode = slots[0].isWeekMode ? "weeks" : "days";

    // Today's slot, when visible (same condition as the Today button).
    const todayIdx = Math.max(0, Math.min(findSlotIndexByDate(new Date()), slots.length - 1));
    const todaySlot = slots[todayIdx];
    if (todaySlot.right > viewLeft && todaySlot.left < viewRight) {
      pendingAnchor.current = { kind: "today", date: new Date(todaySlot.date), offsetFromLeft: todaySlot.left - viewLeft, mode };
      return;
    }

    // First month label in view (the first fully visible slot where the month
    // changes; that is where DatesGoals renders the month name).
    for (let i = 1; i < slots.length; i++) {
      if (slots[i].left < viewLeft) continue;
      if (slots[i].left >= viewRight) break;
      if (slots[i].date.getMonth() !== slots[i - 1].date.getMonth()) {
        pendingAnchor.current = { kind: "month", date: new Date(slots[i].date), offsetFromLeft: slots[i].left - viewLeft, mode };
        return;
      }
    }

    // Fallback: slot under the viewport center.
    const centerPx = viewLeft + el.clientWidth / 2;
    let idx = 0;
    for (let i = 0; i < slots.length; i++) {
      if (slots[i].left <= centerPx) idx = i;
      else break;
    }
    pendingAnchor.current = { kind: "center", date: new Date(slots[idx].date), offsetFromLeft: slots[idx].left - viewLeft, mode };
  }, [dates, findSlotIndexByDate]);

  // Resolve the slot an anchor refers to in the current geometry. A "month"
  // anchor maps to the first slot of that month (where the label renders in
  // both view modes); "today" re-resolves today's slot.
  const findAnchorSlotIndex = useCallback((slots, a) => {
    if (a.kind === "month") {
      for (let i = 0; i < slots.length; i++) {
        if (slots[i].date.getFullYear() === a.date.getFullYear() && slots[i].date.getMonth() === a.date.getMonth()) return i;
      }
    }
    const date = a.kind === "today" ? new Date() : a.date;
    return Math.max(0, Math.min(findSlotIndexByDate(date), slots.length - 1));
  }, [findSlotIndexByDate]);

  // Grow the universe when the user scrolls near an edge. Anchoring on the
  // center date keeps the view visually fixed across the rebuild (including the
  // left-prepend case where every slot position shifts).
  const handleNearEdge = useCallback(
    (side) => {
      if (extendingRef.current) return;
      extendingRef.current = true;
      captureAnchor();
      if (side === "left") {
        universeStart.current = universeStart.current.subtractDays(EXTEND_CHUNK_DAYS, true);
      } else {
        universeEnd.current = universeEnd.current.addDays(EXTEND_CHUNK_DAYS, true);
      }
      buildGrid();
    },
    [buildGrid, captureAnchor]
  );

  const getSlots = useCallback(() => dates.current || [], [dates]);
  const handleWindowChange = useCallback((start, end) => {
    setWindowRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  // Precise "today in view" detection based on the actual viewport (not the
  // buffered window), so the Today button appears the moment today's marker
  // scrolls off-screen.
  const handleMetrics = useCallback(
    (scrollLeft, clientWidth) => {
      const slots = dates.current;
      if (!slots?.length) return;
      const idx = Math.max(0, Math.min(findSlotIndexByDate(new Date()), slots.length - 1));
      const slot = slots[idx];
      const visible = slot.right > scrollLeft && slot.left < scrollLeft + clientWidth;
      setTodayInRange((prev) => (prev === visible ? prev : visible));

      // Strictly visible slot range (no buffer): used by the Team filter to
      // hide members that have no active days in the current viewport.
      let vStart = 0;
      while (vStart < slots.length - 1 && slots[vStart].right <= scrollLeft) vStart++;
      let vEnd = slots.length - 1;
      while (vEnd > vStart && slots[vEnd].left >= scrollLeft + clientWidth) vEnd--;
      setViewportRange((prev) => (prev.start === vStart && prev.end === vEnd ? prev : { start: vStart, end: vEnd }));
    },
    [dates, findSlotIndexByDate]
  );

  const { recompute, scrollToPx, snapToSlotPx } = useTimelineScroll({
    scrollerRef,
    scrollerEl,
    getSlots,
    onWindowChange: handleWindowChange,
    onNearEdge: handleNearEdge,
    onMetrics: handleMetrics,
  });

  // Smooth-scroll the viewport so `date` sits at `align` (0..1) from the left
  // edge (default centered).
  const scrollToDate = useCallback(
    (date, animate = true, align = 0.5) => {
      const slots = dates.current;
      const el = scrollerRef.current;
      if (!slots?.length || !el) return;
      const idx = Math.max(0, Math.min(findSlotIndexByDate(date), slots.length - 1));
      const slot = slots[idx];
      scrollToPx(slot.left - el.clientWidth * align, animate);
    },
    [dates, findSlotIndexByDate, scrollToPx]
  );

  // Jump to today's default placement: in days view the slot three weeks back
  // lands flush at the left edge (three weeks of history), in weeks view
  // today sits a quarter in from the left edge.
  const scrollToToday = useCallback(() => {
    if (viewMode === "days") scrollToDate(new Date().subtractDays(TODAY_HISTORY_DAYS, true), true, 0);
    else scrollToDate(new Date(), true, TODAY_ALIGN);
  }, [viewMode, scrollToDate]);

  // After each rebuild (people set by buildGrid), restore the anchored scroll
  // position (or center today on first load), then refresh the visible window.
  // useLayoutEffect runs before paint so there is no visible jump on prepend.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    const slots = dates.current;
    if (!el || !slots?.length || !people) return;

    // A brand-new scroller node starts at scrollLeft 0; if the node was
    // replaced (subtree remount), redo the initial today-alignment for it.
    if (initializedScrollerEl.current !== el) {
      initializedScrollerEl.current = el;
      didInitialScroll.current = false;
    }

    // On a viewMode change this effect fires once before the grid is rebuilt
    // (slots still have the previous mode's geometry). Bail out so the pending
    // anchor survives until the rebuild-triggered run, which restores it
    // against the new geometry.
    const slotsMode = slots[0].isWeekMode ? "weeks" : "days";
    if (slotsMode !== viewMode) return;

    if (pendingAnchor.current) {
      const a = pendingAnchor.current;
      const idx = findAnchorSlotIndex(slots, a);
      const slot = slots[idx];
      // Put the reference slot back at the same offset from the viewport's
      // left edge. Same-mode rebuilds (e.g. universe prepend) restore
      // pixel-exact; cross-mode switches zoom around the reference point.
      let desired = slot.left - a.offsetFromLeft;

      // The weeks universe is far narrower in pixels than the days universe,
      // so the desired position can fall outside the new scrollable range.
      // Setting scrollLeft directly would get silently clamped by the browser
      // and the view would land on the wrong date. Instead extend the
      // universe toward the needed side and retry on the rebuild-triggered
      // pass, keeping the anchor.
      const contentWidth = slots[slots.length - 1].right;
      const maxScroll = Math.max(0, contentWidth - el.clientWidth);
      const universeDays = Math.max(1, Math.round((universeEnd.current - universeStart.current) / 86400000));
      const pxPerDay = contentWidth / universeDays;
      if (desired > maxScroll) {
        const extraDays = Math.ceil((desired - maxScroll) / pxPerDay) + EXTEND_CHUNK_DAYS;
        universeEnd.current = universeEnd.current.addDays(extraDays, true);
        extendingRef.current = true;
        buildGrid();
        return;
      }
      if (desired < 0) {
        const extraDays = Math.ceil(-desired / pxPerDay) + EXTEND_CHUNK_DAYS;
        universeStart.current = universeStart.current.subtractDays(extraDays, true);
        extendingRef.current = true;
        buildGrid();
        return;
      }

      // Cross-mode restores land on an arbitrary pixel; quantize to a slot
      // boundary ourselves so the scroll-end settle doesn't visibly shift the
      // view right after the switch. Same-mode restores reproduce an
      // already-snapped position and must stay pixel-exact (prepend case).
      el.scrollLeft = (a.mode || "days") === slotsMode ? desired : snapToSlotPx(desired);
      pendingAnchor.current = null;
      didInitialScroll.current = true; // this element now has a valid position
    } else if (!didInitialScroll.current) {
      // Days view lands with three weeks of history left of today; other
      // views place today a quarter in from the left edge.
      const anchorDate = viewMode === "days" ? new Date().subtractDays(TODAY_HISTORY_DAYS, true) : new Date();
      const align = viewMode === "days" ? 0 : TODAY_ALIGN;
      const idx = Math.max(0, Math.min(findSlotIndexByDate(anchorDate), slots.length - 1));
      const slot = slots[idx];
      // Quantize to a slot boundary so the view loads already aligned (the
      // leftmost dot flush with the left edge, nothing clipped).
      el.scrollLeft = snapToSlotPx(Math.max(0, slot.left - el.clientWidth * align));
      didInitialScroll.current = true;
    }

    extendingRef.current = false;
    recompute();
  }, [people, scrollerEl, findSlotIndexByDate, findAnchorSlotIndex, recompute, snapToSlotPx, viewMode, buildGrid]);

  // Team filter: in the default state, hide members that have no active days
  // inside the strictly visible viewport. "Full Team" shows everyone that has
  // been on the project now and previously.
  const peopleInView = useMemo(() => {
    if (!people || showFullTeam) return people;
    return people.filter((peep) => {
      for (let j = viewportRange.start; j <= viewportRange.end; j++) {
        if (peep.days[j]?.isActive) return true;
      }
      return false;
    });
  }, [people, showFullTeam, viewportRange]);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  const fetchData = useCallback(
    async (_startDate) => {
      // Get users with membership in this org (exclude deleted)
      let res1 = await supabase
        .from("memberships")
        .select("*, people(*, days(*, day_type(*)))")
        .eq("org_id", user.org.id)
        .neq("status", "deleted")
        .order("date", { foreignTable: "people.days", ascending: true });
      let res2 = await supabase.from("goals").select().eq("project_id", user.proj.id).not("date", "is", null).order("date");

      // A failed query (e.g. auth token still settling right after login) must
      // not be interpreted as "no project access": that would unmount the grid
      // and, on remount, previously left the scroll listeners dead. Keep the
      // current state and let the next fetch (user refresh) try again.
      if (res1.error || res2.error) return;

      // Guard against orphan memberships where the joined people row is missing
      // (can happen briefly after a placeholder person is deleted, before the
      // cascade has fully propagated to the cached query result).
      const validMemberships = (res1.data || []).filter((m) => m.people);

      // If user has no "joins" or "leaves" days in project, do not allow project access
      setHasProjectAccess(validMemberships.some((m) => m.people.id === user.id && m.people.days.some((day) => day.system_type === "joins" || day.system_type === "leaves")));

      let _peopleAllRawData = validMemberships.map((member) => ({
        ...member.people,
        status: member.status,
        type: member.type,
        days: member.people.days.filter((day) => day.project_id === user.proj.id || day.day_type !== null), // Remove other projects days but allow all sick/vaca/etc days
      }));

      // Measure longest *displayed* name (same format as Row: firstname + " " + last initial)
      const getDisplayName = (p) =>
        (p.firstname || "").trim()
          ? (p.firstname + (p.lastname ? " " + p.lastname.charAt(0) : "")).trim()
          : (p.email || "");
      const newLongest = Math.min(
        _peopleAllRawData?.reduce((acc, curr) => {
          const len = getDisplayName(curr).length;
          return acc < len ? len : acc;
        }, 0) || 10,
        MAX_DISPLAY_NAME_CHARS
      );
      const clamped = Math.max(newLongest, MIN_DISPLAY_NAME_CHARS);
      longestName.current = clamped;
      setLongestNameCharCount(clamped);
      peopleAllRawData.current = _peopleAllRawData;
      goals.current = res2.data;

      // Add today marker
      const todayObj = { id: 0, name: "TODAY", date: new Date().toISOString().split("T")[0], type: "today", disabled: true };
      const todayIndex = goals.current.findIndex((v) => new Date() <= new Date(v.date));
      if (todayIndex > -1) goals.current.splice(todayIndex, 0, todayObj);
      else goals.current.push(todayObj);

      buildGrid(_startDate);
    },
    [longestName, peopleAllRawData, buildGrid, supabase, user, setHasProjectAccess]
  );

  const selectDay = useCallback(
    (dayData, e) => {
      if (selectedDays.current?.constructor !== Array) selectedDays.current = [];
      // Use visual row index (peopleForDots order) so shift+click rectangle matches on-screen when grouped
      const row = dayData.visualRowIndex !== undefined ? dayData.visualRowIndex : dayData.personIndex;
      const col = dayData.dayIndex;
      // Identify the cell by its date stamp (stable across windowing/extension),
      // keeping col only for rectangle math at selection time.
      const stamp = dayData.daystamp;

      if (e?.shiftKey) {
        // Range selection: anchor to clicked cell (inclusive rectangle).
        // Resolve the anchor's current column from its stamp in case the
        // universe was extended/rebuilt between clicks.
        const anchor = selectionAnchorRef.current ?? selectedDays.current[0] ?? { row, col, stamp };
        const anchorColNow = anchor.stamp != null ? dates.current.findIndex((s) => s.date.daystamp() === anchor.stamp) : -1;
        const aCol = anchorColNow > -1 ? anchorColNow : anchor.col;
        const minRow = Math.min(anchor.row, row);
        const maxRow = Math.max(anchor.row, row);
        const minCol = Math.min(aCol, col);
        const maxCol = Math.max(aCol, col);
        selectedDays.current = [];
        for (let r = minRow; r <= maxRow; r++) {
          for (let c = minCol; c <= maxCol; c++) {
            selectedDays.current.push({ row: r, col: c, stamp: dates.current[c]?.date.daystamp() });
          }
        }
      } else {
        // Single click: toggle this cell and set anchor for future shift+click
        const alreadySelected = selectedDays.current.findIndex((day) => day.row === row && day.stamp === stamp);
        if (alreadySelected > -1) {
          selectedDays.current.splice(alreadySelected, 1);
        } else {
          selectedDays.current.push({ row, col, stamp });
        }
        selectionAnchorRef.current = { row, col, stamp };
      }
      setShowDaySelect(selectedDays.current.length);
    },
    [selectedDays, setShowDaySelect, dates]
  );

  const closeDaySelect = useCallback(() => {
    selectedDays.current = null;
    selectionAnchorRef.current = null;
    setShowDaySelect(false);
  }, [setShowDaySelect, selectedDays]);

  const submitDays = useCallback(
    async (typeId, typeName) => {
      let toDelete = [];
      let toUpsert = [];
      let toInsert = [];
      let toClearType = []; // join/leave rows: set day_type to null when choosing "working"
      const peopleForDots = peopleForDotsRef.current || people;
      for (var i = 0; i < selectedDays.current.length; i++) {
        const dayData = selectedDays.current[i];
        const peep = peopleForDots[dayData.row];
        if (!peep) continue;
        const day = (dayData.stamp != null ? peep.days.find((d) => d.daystamp === dayData.stamp) : null) ?? peep.days[dayData.col];
        if (!day) continue;
        var target = {
          person_id: peep.person.id,
          date: day.day.toISOString(),
          day_type: typeId,
        };

        if (day.id && day.day_type) {
          // Previous record exists in db with a type
          if (typeName === "working") {
            if (day.system_type) toClearType.push(day.id); // join/leave: clear type, don't delete row
            else toDelete.push(day.id);
          } else if (day.style !== typeName) toUpsert.push({ ...target, id: day.id });
        } else if (day.id) {
          // Existing row (e.g. join/leave) but no type set — update it so one row has both system_type and day_type
          if (typeName !== "working") toUpsert.push({ ...target, id: day.id });
        } else {
          if (typeName !== "working") toInsert.push(target);
        }
      }

      if (toDelete.length) await supabase.from("days").delete().in("id", toDelete);
      if (toUpsert.length) await supabase.from("days").upsert(toUpsert);
      if (toInsert.length) await supabase.from("days").insert(toInsert);
      if (toClearType.length) await supabase.from("days").update({ day_type: null }).in("id", toClearType);

      closeDaySelect();
      if (toUpsert.length || toDelete.length || toInsert.length || toClearType.length) fetchData(startDate.current);
    },
    [people, selectedDays, closeDaySelect, fetchData, supabase]
  );

  const submitGoal = useCallback(
    async (data, keepOpen) => {
      if (data) {
        await supabase.from("goals").update(data).eq("id", data.id);
      } else if (!editingGoal.current.id) {
        const res = await supabase
          .from("goals")
          .insert({
            date: editingGoal.current.date.toISOString(),
            project_id: user.proj.id,
            type: "minor",
            private: false,
          })
          .select();
        editingGoal.current = res.data[0];
        newlyCreatedGoalIdRef.current = res.data[0].id;
        setShowEditGoal(true);
      }

      if (!keepOpen) {
        editingGoal.current = null;
        setShowEditGoal(false);
      }
      fetchData(startDate.current);
    },
    [editingGoal, user, fetchData, supabase]
  );

  const deleteGoal = useCallback(
    async (id) => {
      // If Goal has Work items attached to it in Work, deny user Goal deletion
      // (legacy: older schemas used "stories", current schema uses unified "work" with goal_id)
      let hasWorkAttached = false;
      let checkError = null;

      // Primary: unified work table
      const workCheck = await supabase.from("work").select("id").eq("goal_id", id).limit(1);
      if (workCheck?.error) {
        checkError = workCheck.error;
      } else {
        hasWorkAttached = (workCheck?.data?.length || 0) > 0;
      }

      // Fallback (legacy): stories table
      if (checkError?.code === "42P01") {
        const storiesCheck = await supabase.from("stories").select("id").eq("goal", id).limit(1);
        if (storiesCheck?.error) {
          checkError = storiesCheck.error;
        } else {
          hasWorkAttached = (storiesCheck?.data?.length || 0) > 0;
          checkError = null;
        }
      }

      if (checkError) {
        console.error("Failed checking work items for goal delete", checkError);
        window.alert("Couldn't delete this goal right now. Please try again.");
        return;
      }

      if (hasWorkAttached) {
        const goal = goals.current.find((_goal) => _goal.id === id);
        window.alert("The goal " + goal.name + " contains Stories in it, found under Work.\n\nPlease delete those first if you wish to remove this goal.");
      } else {
        if (id) await supabase.from("goals").delete().eq("id", id);
      }

      if (!hasWorkAttached) {
        editingGoal.current = null;
        setShowEditGoal(false);
        fetchData(startDate.current);
      }
    },
    [supabase, fetchData, editingGoal, setShowEditGoal]
  );

  const closeGoal = useCallback(
    (payload) => {
      if (payload?.deleteNew && editingGoal.current?.id) {
        deleteGoal(editingGoal.current.id);
      } else {
        editingGoal.current = null;
        setShowEditGoal(false);
      }
      newlyCreatedGoalIdRef.current = null;
    },
    [editingGoal, deleteGoal, setShowEditGoal]
  );

  const closeAddPerson = useCallback(() => {
    addPersonDate.current = null;
    setShowAddPerson(false);
  }, [setShowAddPerson, addPersonDate]);

  const getDayBoundries = useCallback(
    (personId, date) => {
      const person = peopleAllRawData.current.find((peep) => peep.id === personId);
      // Include all join/leave days (with or without day_type) so dragging works after setting e.g. Sick
      const daysFiltered = person.days.filter((day) => (day.system_type === "joins" || day.system_type === "leaves") && day.project_id === user.proj.id);
      const dateStr = date.getISODate();
      let dayIdx = daysFiltered.findIndex((d) => (d.date && d.date.split("T")[0]) === dateStr || d.date === dateStr);
      let day = daysFiltered[dayIdx];

      let leftBoundryIdx = -1;
      let leftBoundryDay = null;
      let rightBoundryIdx = -1;
      let rightBoundryDay = null;
      if (!day) {
        // date is arbritrary, not existing in db

        // search left from arbritrary date
        for (let i = daysFiltered.length - 1; i >= 0; i--) {
          if (new Date(daysFiltered[i].date) < date) {
            leftBoundryIdx = dates.current.findIndex((d) => d.date.getISODate() === daysFiltered[i].date);
            leftBoundryDay = daysFiltered[i];
            break;
          }
        }
        // search right from arbritrary date
        for (let j = 0; j < daysFiltered.length; j++) {
          if (new Date(daysFiltered[j].date) > date) {
            rightBoundryIdx = dates.current.findIndex((d) => d.date.getISODate() === daysFiltered[j].date);
            rightBoundryDay = daysFiltered[j];
            break;
          }
        }
      } else {
        // date exists in db
        leftBoundryIdx = dayIdx > 0 ? dates.current.findIndex((d) => d.date.getISODate() === daysFiltered[dayIdx - 1].date) : -1;
        rightBoundryIdx = dayIdx < daysFiltered.length - 1 ? dates.current.findIndex((d) => d.date.getISODate() === daysFiltered[dayIdx + 1].date) : -1;
        leftBoundryDay = daysFiltered[dayIdx - 1];
        rightBoundryDay = daysFiltered[dayIdx + 1];
      }
      return {
        leftBoundry: leftBoundryIdx > -1 ? { index: leftBoundryIdx, day: leftBoundryDay } : null,
        rightBoundry: rightBoundryIdx > -1 ? { index: rightBoundryIdx, day: rightBoundryDay } : null,
        day,
      };
    },
    [peopleAllRawData, dates, user.proj.id]
  );

  const submitAddTimeline = useCallback(
    async (newTimeline, date) => {
      // Filter away suggested people that are already active on that date in the project
      const newTimelineFiltered = newTimeline.filter((newPerson) => {
        if (!newPerson.id) return true;
        const newPersonFull = peopleAllFormatted.current.find((peep) => peep.person.id === newPerson.id);
        return !newPersonFull.activeRanges.some((range) => (!range[1] && date > range[0]) || (range[1] && date > range[0] && date < range[1]));
      });

      // Trial mode: any entry with an email implies an invite, which requires
      // a real account. Show the sign-up nudge instead of pretending it worked.
      if (isTrial && newTimelineFiltered.some((p) => p.email && p.email.trim())) {
        closeAddPerson();
        setShowTrialNudge(true);
        return;
      }

      // Execute invite/add to project
      for (var i = 0; i < newTimelineFiltered.length; i++) {
        const newPerson = newTimelineFiltered[i];
        const newPersonExist = peopleAllFormatted.current.find((peep) => peep.person.id === newPerson.id || peep.person.email === newPerson.email.trim()); // Check if person exist in this org

        if (!newPersonExist) {
          // Person new to org
          let personId = null;
          let status = null;

          if (newPerson.email) {
            // Email included, send direct invite
            let redirectTo = "";

            // Check if email already exists in other orgs
            const { data } = await supabase.from("people").select().eq("email", newPerson.email);

            if (data?.length) {
              // User does exist in other org(s)
              if (!data[0]?.current_org_id) {
                // ..though has never logged in (likely status "placeholder")
                await supabase.from("people").update({ current_org_id: user.org.id, current_proj_id: user.proj.id }).eq("email", newPerson.email);
                redirectTo = "password";
              }
              personId = data[0].id;
            } else {
              // User is completely new to Peepz
              const userRes = await supabase.from("people").insert({ 
                firstname: newPerson.firstname?.trim() || '', 
                lastname: newPerson.lastname?.trim() || '', 
                current_org_id: user.org.id, 
                current_proj_id: user.proj.id 
              }).select();
              personId = userRes.data[0].id;
              redirectTo = "password";
            }

            await fetch("/.netlify/functions/inviteuser", {
              method: "POST",
              body: JSON.stringify({ 
                email: newPerson.email, 
                id: personId, 
                name: `${newPerson.firstname?.trim() || ''} ${newPerson.lastname?.trim() || ''}`.trim(),
                firstname: newPerson.firstname?.trim() || '',
                lastname: newPerson.lastname?.trim() || '',
                redirectTo: redirectTo, 
                inviter: user.firstname + " " + user.lastname 
              }),
            });
            // TODO: if error 422 - user already exists - make sure that user still gets invite email, but sent explicitely since error makes email sending fail
            status = "invited";
          } else {
            // No email, create placeholder for person to invite them later
            const { data } = await supabase.from("people").insert({ 
              firstname: newPerson.firstname?.trim() || '', 
              lastname: newPerson.lastname?.trim() || '' 
            }).select();
            personId = data[0].id;
            status = "placeholder";
          }

          await supabase.from("memberships").insert({ person_id: personId, org_id: user.org.id, type: "member", status: status, project_invite: user.proj.id });
          await supabase.from("days").insert({ person_id: personId, date: date.toISOString(), system_type: "joins", project_id: user.proj.id });
        } else {
          // Person exists in org
          // If joining with a join to the right, pull that ont in instead
          const dayBounds = getDayBoundries(newPersonExist.person.id, date);
          if (dayBounds.rightBoundry?.day.system_type === "joins") {
            await supabase.from("days").update({ date: date.toISOString() }).eq("id", dayBounds.rightBoundry.day.id);
          } else {
            await supabase.from("days").insert({ person_id: newPersonExist.person.id, date: date.toISOString(), system_type: "joins", project_id: user.proj.id });
          }
        }
      }
      closeAddPerson();
      fetchData(startDate.current);
    },
    [peopleAllFormatted, closeAddPerson, supabase, user, getDayBoundries, fetchData, isTrial]
  );

  const personLeaveProject = useCallback(
    async (personId, date) => {
      // Check for any pre-existing "leaves" day to the right of date
      const person = peopleAllRawData.current.find((peep) => peep.id === personId);
      const daysFiltered = person.days.filter((d) => d.system_type === "leaves" && new Date(d.date) > date.day && d.project_id === user.proj.id);
      const existingLeaveDay = daysFiltered.length ? daysFiltered[0] : null;

      if (existingLeaveDay) {
        await supabase.from("days").update({ date: date.day.toISOString() }).eq("id", existingLeaveDay.id);
      } else {
        await supabase.from("days").insert({ person_id: personId, date: date.day.toISOString(), system_type: "leaves", project_id: user.proj.id });
      }
      closeDaySelect();
      fetchData(startDate.current);
    },
    [peopleAllRawData, supabase, closeDaySelect, fetchData, user.proj.id]
  );

  const handleMoveJoinLeaveDay = useCallback(
    (e) => {
      if (!movingDay.current) return;
      // We use requestAnimationFrame to throttle updates
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      animationFrameId.current = requestAnimationFrame(() => {
        if (movingDay.current) {
          const containerLeft = dotsContainerRef.current.getBoundingClientRect().left;
          const xPosition = e.clientX - containerLeft - movingDay.current.xOffset + BOX_SIZE / 2;

          // Calculate which div the mouse is over
          let newIndex = -1;
          for (let i = 0; i < dates.current.length; i++) {
            const d = dates.current[i];
            if (d.left < xPosition && xPosition < d.right) {
              newIndex = i;
              break;
            }
          }

          const inLeftBoundry = movingDay.current.leftBoundryIdx === -1 || newIndex > movingDay.current.leftBoundryIdx;
          const inRightBoundry = movingDay.current.rightBoundryIdx === -1 || newIndex < movingDay.current.rightBoundryIdx;

          if (newIndex !== xIndexRef.current && xPosition > 0 && newIndex !== -1 && inLeftBoundry && inRightBoundry) {
            xIndexRef.current = newIndex;
            let person = peopleAllRawData.current.find((peep) => peep.id === movingDay.current.person.id);
            let day = person.days.find((d) => d.id === movingDay.current.id);
            const newDate = dates.current[newIndex].date.getISODate();

            day.date = newDate;
            // Preview: show destination as normal join/leave (no day_type). movingDay.current keeps original day_type for the drop handler.
            if (movingDay.current.day_type != null) day.day_type = null;

            targetMovedJoinLeaveDay.current = dates.current[newIndex].date;

            buildGrid(startDate.current);
          }
        }
      });
    },
    [dotsContainerRef, movingDay, dates, xIndexRef, buildGrid]
  );

  const movedJoinLeaveDay = useCallback(async () => {
    if (targetMovedJoinLeaveDay.current) {
      const hadDayType = movingDay.current.day_type != null;
      const dayTypeId = movingDay.current.day_type?.id ?? movingDay.current.day_type;
      const originalDate = movingDay.current.originalDate;

      // When the dot had a non-working status, leave it at the original date (don't move it with the join/leave)
      if (hadDayType && originalDate) {
        await supabase.from("days").insert({
          person_id: movingDay.current.person_id,
          date: originalDate,
          day_type: dayTypeId,
        });
      }

      let doUpdate = true;
      // If moved next to joins/leaves, then merge the two
      if (movingDay.current.system_type === "joins") {
        if (movingDay.current.leftBoundryIdx !== -1 && xIndexRef.current === movingDay.current.leftBoundryIdx + 1) {
          // Moved a "joins" left to the date just right of a "leaves", delete both
          const bounds = getDayBoundries(movingDay.current.person_id, targetMovedJoinLeaveDay.current);
          await supabase.from("days").delete().in("id", [bounds.day.id, bounds.leftBoundry.day.id]);
          doUpdate = false;
        }
      } else if (movingDay.current.system_type === "leaves") {
        if (movingDay.current.rightBoundryIdx !== -1 && xIndexRef.current === movingDay.current.rightBoundryIdx - 1) {
          // Moved a "leaves" to the date just left of a "joines", delete both
          const bounds = getDayBoundries(movingDay.current.person_id, targetMovedJoinLeaveDay.current);
          await supabase.from("days").delete().in("id", [bounds.day.id, bounds.rightBoundry.day.id]);
          doUpdate = false;
        }
      }

      if (doUpdate) {
        await supabase
          .from("days")
          .update({
            date: targetMovedJoinLeaveDay.current.getISODate(),
            ...(hadDayType && { day_type: null }),
          })
          .eq("id", movingDay.current.id);
      }
      fetchData(startDate.current);
    }

    document.removeEventListener("mousemove", handleMoveJoinLeaveDay);
    document.removeEventListener("mouseup", movedJoinLeaveDay);
    targetMovedJoinLeaveDay.current = null;
    movingDay.current = null;
  }, [fetchData, targetMovedJoinLeaveDay, movingDay, xIndexRef, getDayBoundries, supabase, handleMoveJoinLeaveDay]);

  const startDragJoinLeaveDay = useCallback(
    (personId, date, xOffset) => {
      const person = peopleAllRawData.current.find((peep) => peep.id === personId);
      const bounds = getDayBoundries(personId, date);
      const day = bounds.day;
      const originalDateStr = day.date ? (day.date.split("T")[0] || day.date) : null;
      movingDay.current = {
        ...day,
        xOffset,
        person,
        leftBoundryIdx: bounds.leftBoundry?.index || -1,
        rightBoundryIdx: bounds.rightBoundry?.index || -1,
        originalDate: originalDateStr,
      };

      document.addEventListener("mousemove", handleMoveJoinLeaveDay);
      document.addEventListener("mouseup", movedJoinLeaveDay);
    },
    [peopleAllRawData, movingDay, movedJoinLeaveDay, handleMoveJoinLeaveDay, getDayBoundries]
  );

  const removePerson = useCallback(
    async (personId, name) => {
      // Prevent removing the last owner
      const owners = people.filter((p) => p.person.type === "owner");
      if (owners.length <= 1 && owners[0].person.id === personId) {
        window.alert("You cannot remove the last remaining Owner.");
        return;
      }
      if (window.confirm("DANGER!\n\nAre you sure you'd like to remove all of " + name + "'s history of working on this project?")) {
        const { data } = await supabase.from("people").select().eq("id", personId);
        if (!data[0]?.uid && !data[0]?.email) {
          // If user is placeholder, i.e. not active, remove person completely.
          await supabase.from("people").delete().eq("id", personId);
        } else {
          await supabase.from("days").delete().eq("person_id", personId).eq("project_id", user.proj.id);
        }
        fetchData(startDate.current);
      }
    },
    [supabase, fetchData, user.proj.id, people]
  );

  const inviteUser = useCallback(
    async (id, email, name) => {
      if (isTrial) {
        setShowTrialNudge(true);
        return;
      }
      // let res1 = await supabase.from("memberships").select("*, people(*, days(*, day_type(*)))").eq("org_id", user.org.id);
      const { data } = await supabase.from("people").select().eq("id", id);
      let redirectTo = "";

      if (!data[0].current_org_id) {
        // User is completely new to Peepz, redirect new user to password setting in invite email's link
        await supabase.from("people").update({ current_org_id: user.org.id, current_proj_id: user.proj.id }).eq("id", id);
        redirectTo = "password";
      }

      await supabase.from("memberships").update({ status: "invited", project_invite: user.proj.id }).eq("person_id", id).eq("org_id", user.org.id);

      await fetch("/.netlify/functions/inviteuser", {
        method: "POST",
        body: JSON.stringify({ email: email, id: id, name: name, redirectTo: redirectTo, inviter: user.firstname + " " + user.lastname }),
      });
      fetchData(startDate.current);
    },
    [supabase, user, fetchData, isTrial]
  );

  const closeInviteUser = useCallback(() => {
    setShowInviteUser(false);
    fetchData(startDate.current);
  }, [setShowInviteUser, fetchData]);

  const toggleGroupBy = useCallback(async () => {
    const newGroupBy = groupBy === 'noGroup' ? 'discipline' : 'noGroup';
    
    // Set flag to prevent useEffect from overriding
    userChangingGroupBy.current = true;
    
    // Update state immediately
    setGroupBy(newGroupBy);
    
    // Save or delete the setting based on the new mode
    if (newGroupBy === 'noGroup') {
      // Delete the setting when in noGroup mode (default)
      if (supabase && user?.proj?.id && user?.id) {
        const { error } = await supabase.from('settings').delete().eq('name', 'timelineGroupBy').eq('project_id', user.proj.id).eq('user_id', user.id);
        if (error) console.error('Error deleting groupBy setting:', error);
        else {
          // Refresh user data to update cached settings
          refresh();
        }
      }
    } else {
      // Save the setting for discipline mode
      if (supabase && user?.proj?.id && user?.id) {
        // Check if groupBy setting already exists for this user/project
        const { data: existingSetting } = await supabase
          .from('settings')
          .select('id')
          .eq('name', 'timelineGroupBy')
          .eq('project_id', user.proj.id)
          .eq('user_id', user.id);
        let error;
        if (existingSetting && existingSetting.length > 0) {
          // Update existing setting
          const result = await supabase
            .from('settings')
            .update({ value: newGroupBy })
            .eq('id', existingSetting[0].id);
          error = result.error;
        } else {
          // Insert new setting
          const result = await supabase.from('settings').insert({
            name: 'timelineGroupBy',
            value: newGroupBy,
            project_id: user.proj.id,
            user_id: user.id,
          });
          error = result.error;
        }
        
        if (error) console.error('Error saving groupBy setting:', error);
        else {
          // Refresh user data to update cached settings
          refresh();
        }
      }
    }
    
    // Reset flag after a short delay to allow useEffect to run again
    setTimeout(() => {
      userChangingGroupBy.current = false;
    }, 100);
  }, [groupBy, supabase, user?.proj?.id, refresh, user?.id]);

  const togglePrivateGoalsFilter = useCallback(async () => {
    const newShowPrivateGoals = !showPrivateGoals;
    userChangingPrivateGoals.current = true;
    setShowPrivateGoals(newShowPrivateGoals);

    if (supabase && user?.proj?.id && user?.id) {
      const { data: existingSetting } = await supabase
        .from("settings")
        .select("id")
        .eq("name", "timelineShowPrivateGoals")
        .eq("project_id", user.proj.id)
        .eq("user_id", user.id);

      const settingValue = newShowPrivateGoals ? "true" : "false";
      let error;
      if (existingSetting && existingSetting.length > 0) {
        const result = await supabase.from("settings").update({ value: settingValue }).eq("id", existingSetting[0].id);
        error = result.error;
      } else {
        const result = await supabase.from("settings").insert({
          name: "timelineShowPrivateGoals",
          value: settingValue,
          project_id: user.proj.id,
          user_id: user.id,
        });
        error = result.error;
      }

      if (error) console.error("Error saving private goals filter setting:", error);
      else refresh();
    }

    setTimeout(() => {
      userChangingPrivateGoals.current = false;
    }, 100);
  }, [showPrivateGoals, supabase, user?.proj?.id, user?.id, refresh]);

  const toggleTeamFilter = useCallback(async () => {
    const newShowFullTeam = !showFullTeam;
    userChangingTeamFilter.current = true;
    setShowFullTeam(newShowFullTeam);

    if (supabase && user?.proj?.id && user?.id) {
      const { data: existingSetting } = await supabase
        .from("settings")
        .select("id")
        .eq("name", "timelineShowFullTeam")
        .eq("project_id", user.proj.id)
        .eq("user_id", user.id);

      const settingValue = newShowFullTeam ? "true" : "false";
      let error;
      if (existingSetting && existingSetting.length > 0) {
        const result = await supabase.from("settings").update({ value: settingValue }).eq("id", existingSetting[0].id);
        error = result.error;
      } else {
        const result = await supabase.from("settings").insert({
          name: "timelineShowFullTeam",
          value: settingValue,
          project_id: user.proj.id,
          user_id: user.id,
        });
        error = result.error;
      }

      if (error) console.error("Error saving team filter setting:", error);
      else refresh();
    }

    setTimeout(() => {
      userChangingTeamFilter.current = false;
    }, 100);
  }, [showFullTeam, supabase, user?.proj?.id, user?.id, refresh]);

  const cycleViewMode = useCallback(async () => {
    const newMode = viewMode === 'days' ? 'weeks' : 'days';
    
    // Set flag to prevent useEffect from overriding
    userChangingViewMode.current = true;

    // Preserve the visual reference point across the geometry rebuild
    captureAnchor();
    if (newMode === "weeks") {
      // Remember the exact days-view anchor: weeks geometry is too coarse
      // (slot snapping shifts the view by up to half a week) to reconstruct
      // it precisely when switching back.
      daysAnchor.current = pendingAnchor.current ? { ...pendingAnchor.current } : null;
    } else if (daysAnchor.current) {
      // Back to days: if the stored days reference is still at (nearly) the
      // same x in the weeks view (within snap noise, less than one week slot
      // of movement), the user hasn't scrolled away, so return to the exact
      // stored days position instead of re-deriving one.
      const el = scrollerRef.current;
      const slots = dates.current;
      if (el && slots?.length) {
        const d = daysAnchor.current;
        const refIdx = findAnchorSlotIndex(slots, d);
        const curOffset = slots[refIdx].left - el.scrollLeft;
        if (Math.abs(curOffset - d.offsetFromLeft) < 30) pendingAnchor.current = { ...d };
      }
    }

    // Update state immediately
    setViewMode(newMode);
    
    // Save or delete the setting based on the new mode
    if (newMode === 'days') {
              // Delete the setting when in days mode (default)
        if (supabase && user?.proj?.id && user?.id) {
          const { error } = await supabase.from('settings').delete().eq('name', 'timelineViewMode').eq('project_id', user.proj.id).eq('user_id', user.id);
          if (error) console.error('Error deleting viewMode setting:', error);
          else {
            // Refresh user data to update cached settings
            refresh();
          }
        }
    } else {
              // Save the setting for weeks mode
        if (supabase && user?.proj?.id && user?.id) {
          // Check if viewMode setting already exists for this user/project
          const { data: existingSetting } = await supabase
            .from('settings')
            .select('id')
            .eq('name', 'timelineViewMode')
            .eq('project_id', user.proj.id)
            .eq('user_id', user.id);
          let error;
          if (existingSetting.length > 0) {
            // Update existing setting
            const result = await supabase
              .from('settings')
              .update({ value: newMode })
              .eq('id', existingSetting[0].id);
            error = result.error;
          } else {
            // Insert new setting
            const result = await supabase.from('settings').insert({
              name: 'timelineViewMode',
              value: newMode,
              project_id: user.proj.id,
              user_id: user.id,
            });
            error = result.error;
          }
          
          if (error) console.error('Error saving viewMode setting:', error);
          else {
            // Refresh user data to update cached settings
            refresh();
          }
        }
    }
    
    // Reset flag after a short delay to allow useEffect to run again
    setTimeout(() => {
      userChangingViewMode.current = false;
    }, 100);
  }, [viewMode, supabase, user?.proj?.id, refresh, user?.id, captureAnchor, findAnchorSlotIndex]);

  const resizeDebounce = useCallback((func) => {
    var timer;
    return function (event) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(func, 100, event);
    };
  }, []);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  useEffect(() => {
    showWeekends.current = user?.proj?.settings?.find((s) => s.name === "showweekends")?.value === "true" || false;
    
    // Load viewMode from settings when user/project changes
    // But don't override if user is actively changing the view mode
    if (user?.proj?.settings && !userChangingViewMode.current) {
      const savedViewMode = user.proj.settings.find((s) => s.name === "timelineViewMode")?.value;
      // Only load from settings when in default 'days' state.
      // 'months' was removed as a view mode; treat a stale saved value as weeks.
      if (viewMode === 'days' && (savedViewMode === 'weeks' || savedViewMode === 'months')) {
        setViewMode('weeks');
      }
    }
    
    // Load groupBy from settings when user/project changes
    // But don't override if user is actively changing the groupBy
    if (user?.proj?.settings && !userChangingGroupBy.current) {
      const savedGroupBy = user.proj.settings.find((s) => s.name === "timelineGroupBy")?.value;
      // Only load from settings when in default 'noGroup' state
      if (groupBy === 'noGroup' && savedGroupBy) {
        if (savedGroupBy === 'discipline') {
          setGroupBy('discipline');
        }
      }
    }

    // Load private goals filter from settings when user/project changes
    if (user?.proj?.settings && !userChangingPrivateGoals.current) {
      const savedPrivateGoalsFilter = user.proj.settings.find((s) => s.name === "timelineShowPrivateGoals")?.value;
      setShowPrivateGoals(savedPrivateGoalsFilter === "true"); // default: private goals hidden
    }

    // Load team filter from settings when user/project changes
    if (user?.proj?.settings && !userChangingTeamFilter.current) {
      const savedTeamFilter = user.proj.settings.find((s) => s.name === "timelineShowFullTeam")?.value;
      setShowFullTeam(savedTeamFilter === "true"); // default: filtered (only members active in view)
    }
    
    fetchData(startDate.current);
  }, [fetchData, user, hideDeparted, viewMode, groupBy]);

  // Slot geometry is width-independent, so a resize only needs to refresh the
  // visible slot window (clientWidth changed), not rebuild the universe.
  useEffect(() => {
    const handleResize = resizeDebounce(() => recompute());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [resizeDebounce, recompute]);

  useGSAP(
    () => {
      const hasNonames = document.getElementsByClassName("nonameribbon").length > 0;
      if (!loading) {
        tl.current = gsap
          .timeline()
          .from(".dot", {
            scale: 0,
            duration: 0.06,
            stagger: 0.0011,
          })
          .fromTo(
            ".ribbon",
            {
              opacity: 0,
              y: -100,
            },
            {
              opacity: 1,
              y: 0,
              duration: 0.14,
              ease: "power4.out",
              stagger: 0.04,
            },
            0.15
          )
          .fromTo(
            ".ribbon[data-past-goal='true']",
            {
              opacity: 0.3,
              y: -100,
            },
            {
              opacity: 0.3,
              y: 0,
              duration: 0.14,
              ease: "power4.out",
              stagger: 0.09,
            },
            0.15
          );
        if (hasNonames) tl.current.fromTo(".nonameribbon", { opacity: 0, y: -50 }, { opacity: 0.6, y: 0, duration: 0.8, ease: "power2.out" });
      }
    },
    { dependencies: [loading] }
  );

  // Keep ref in sync with state so the global mousemove listener can read the latest value without re-registering
  useEffect(() => {
    hoveredPersonIdRef.current = hoveredPersonId;
  }, [hoveredPersonId]);

  // Highlight the row whose Y range contains the mouse, based on mouse position (like the horizontal + button).
  // This replaces per-dot mouseenter/mouseleave which flickered due to the small gaps between dots.
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (rowHoverAnimationFrameId.current) {
        cancelAnimationFrame(rowHoverAnimationFrameId.current);
      }
      rowHoverAnimationFrameId.current = requestAnimationFrame(() => {
        const gridEl = dotsGridRef.current;
        const areaEl = rowsAreaRef.current;
        if (!gridEl || !areaEl) return;

        const gridRect = gridEl.getBoundingClientRect();
        const areaRect = areaEl.getBoundingClientRect();
        const yPosition = e.clientY - gridRect.top;

        const outOfBounds =
          yPosition < 0 ||
          yPosition > gridRect.height ||
          e.clientX < areaRect.left ||
          e.clientX > areaRect.right;

        if (outOfBounds) {
          if (hoveredPersonIdRef.current !== null) setHoveredPersonId(null);
          return;
        }

        const peopleForDots = peopleForDotsRef.current || [];
        const personYOffsets = personYOffsetsRef.current || {};
        const rowStride = BOX_SIZE + GRID_SPACE_Y;
        let newHoveredId = null;
        for (let i = 0; i < peopleForDots.length; i++) {
          const offset = personYOffsets[i] || 0;
          if (yPosition >= offset && yPosition < offset + rowStride) {
            newHoveredId = peopleForDots[i]?.person?.id ?? null;
            break;
          }
        }

        if (newHoveredId !== hoveredPersonIdRef.current) {
          setHoveredPersonId(newHoveredId);
        }
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (rowHoverAnimationFrameId.current) {
        cancelAnimationFrame(rowHoverAnimationFrameId.current);
      }
    };
  }, []);

  useEffect(() => {
    async function checkHideGottaGoal() {
      if (!supabase || !user?.proj?.id || !user?.id) return;
      const { data } = await supabase
        .from('settings')
        .select('id')
        .eq('name', 'hideGottaGoal')
        .eq('project_id', user.proj.id)
        .eq('user_id', user.id);
      if (data && data.length > 0) setHideGottaGoal(true);
    }
    checkHideGottaGoal();
  }, [supabase, user]);

  // Load disciplines for grouping
  useEffect(() => {
    if (!supabase || !user?.proj?.id || !user?.proj?.type) {
      setDisciplines([]);
      return;
    }

    const loadDisciplines = async () => {
      const projectType = user.proj.type;
      const defaultDisciplines = getDefaultProjectData(projectType).map(({ doDs, ...discipline }) => discipline);

      // Try to load custom disciplines from settings
      const { data: setting } = await supabase
        .from("settings")
        .select("value")
        .eq("project_id", user.proj.id)
        .eq("name", "project_disciplines")
        .maybeSingle();

      let availableDisciplines = defaultDisciplines;
      if (setting?.value) {
        try {
          const customDisciplines = JSON.parse(setting.value);
          const defaultMap = new Map(defaultDisciplines.map(d => [d.id, d]));
          const merged = customDisciplines.map(customDiscipline => {
            const defaultDiscipline = defaultMap.get(customDiscipline.id);
            return defaultDiscipline ? { ...defaultDiscipline, ...customDiscipline } : customDiscipline;
          });
          merged.sort((a, b) => (a.order || 0) - (b.order || 0));
          availableDisciplines = merged;
        } catch (e) {
          console.error("Failed to parse project_disciplines setting:", e);
        }
      }

      setDisciplines(availableDisciplines);
    };

    loadDisciplines();
  }, [supabase, user]);

  const closeGottaGoal = async () => {
    if (!supabase || !user?.proj?.id || !user?.id) return;
    await supabase.from('settings').upsert({
      name: 'hideGottaGoal',
      value: '1',
      project_id: user.proj.id,
      user_id: user.id,
    });
    setHideGottaGoal(true);
  };

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  // Name column = longest name width + 20px gap + ellipsis button (26px)
  const nameColumnWidth = longestNameCharCount * FONT_WIDTH + NAME_COLUMN_GAP + ELLIPSIS_BUTTON_WIDTH;

  // When the Team filter leaves no visible rows, both the dots grid and the
  // name column reserve the height the full list would occupy, so horizontal
  // scrolling keeps working and the buttons anchored above the column
  // (arrows, plus, Today) stay in place.
  const noVisibleRows = peopleInView != null && peopleInView.length === 0;
  const emptyListHeight = Math.max((people?.length || 0) * (BOX_SIZE + GRID_SPACE_Y), MIN_ROWS_AREA_HEIGHT);

  // Full scrollable width of the date universe (sets the scroller's extent).
  const universeWidth =
    dates.current && dates.current.length ? dates.current[dates.current.length - 1].right + BOX_SIZE : "100%";

  // Today's date, shifted back to Friday when it falls on a weekend (weekends
  // may be hidden in the grid).
  const getWeekdayToday = () => {
    const today = new Date();
    const day = today.getDay();
    if (day === 0) today.setDate(today.getDate() - 2);
    else if (day === 6) today.setDate(today.getDate() - 1);
    return today;
  };

  const formatDisplayName = (person) => {
    const n = (person.firstname || "").trim()
      ? (person.firstname + (person.lastname ? " " + person.lastname.charAt(0) : "")).trim()
      : (person.email || "");
    return n.length > MAX_DISPLAY_NAME_CHARS ? n.slice(0, MAX_DISPLAY_NAME_CHARS - 3) + "..." : n;
  };

  if (!hasProjectAccess) {
    return (
      <center style={{ height: "100%", marginTop: 100 }}>
        <h2>No active projects</h2>
        <br />
        <p>Let's create your own project to get building!</p>
      </center>
    );
  }

  return (
    <div>
      {isTrial && (
        <WelcomePopup
          onAddTeammate={() => {
            addPersonDate.current = getWeekdayToday();
            setShowAddPerson(true);
          }}
          onAddedSampleTeam={() => fetchData(startDate.current)}
        />
      )}
      {showTrialNudge && (
        <TrialNudge
          mode="modal"
          title="Sign up to invite teammates"
          body="In trial mode your teammates are stored only on this device. Sign up to invite people by email."
          onClose={() => setShowTrialNudge(false)}
        />
      )}
      <GoalPopup showEditGoal={showEditGoal} editingGoal={editingGoal.current} isNewGoal={editingGoal.current?.id === newlyCreatedGoalIdRef.current} submitGoal={submitGoal} closeGoal={closeGoal} deleteGoal={deleteGoal} disciplines={disciplines} peopleAllFormatted={peopleAllFormatted.current} />
      <AddPersonPopup
        showAddPerson={showAddPerson}
        addPersonDate={addPersonDate.current}
        closeAddPerson={closeAddPerson}
        submitAddTimeline={submitAddTimeline}
        list={peopleAllFormatted?.current?.map((p) => ({ value: p.person.firstname + (p.person.lastname ? " " + p.person.lastname : ""), id: p.person.id }))}
      />
      <InviteUserPopup showInviteUser={showInviteUser} invitingUser={invitingUser.current} submitInviteCb={inviteUser} closeInviteUserCb={closeInviteUser} people={people} />

      {/* Top panel to select status for selected days in the grid */}
      <DaySelect
        show={showDaySelect}
        fontWidth={FONT_WIDTH}
        clickCb={submitDays}
        closeCb={closeDaySelect}
        leaveDate={
          showDaySelect && selectedDays.current?.length && people?.length
            ? (() => {
                const sel = selectedDays.current[0];
                const peep = (peopleForDotsRef.current || people)[sel.row];
                const d = (sel.stamp != null ? peep?.days.find((dd) => dd.daystamp === sel.stamp) : null) ?? peep?.days[sel.col];
                return d?.day ?? null;
              })()
            : null
        }
        leaveProjectCb={() => {
          if (!selectedDays.current?.length || !people?.length) return;
          const first = selectedDays.current[0];
          const peopleForDots = peopleForDotsRef.current || people;
          const peep = peopleForDots[first.row];
          if (!peep) return;
          const day = (first.stamp != null ? peep.days.find((dd) => dd.daystamp === first.stamp) : null) ?? peep.days[first.col];
          if (!day) return;
          personLeaveProject(peep.person.id, day);
        }}
        canLeave={roleAccess("manager")}
      />

      {/* Tutorial to add a milestone and learn to use date + button */}
      {goals?.current?.length <= 1 && !hideGottaGoal && (
        <Flex gap={1} style={{ marginLeft: "36%", alignItems: "start", position: "absolute", top: 30, minWidth: 320 }}>
          <div style={{ marginRight: -10, zIndex: -10 }}>
            <img alt="arrow" src={arrow} style={{ marginTop: 37, width: 120, transform: "rotate(-10deg) scaleX(-100%) scaleY(-100%) scale(0.8)" }} />
          </div>
          <div style={{ marginTop: 10, userSelect: "none", position: 'relative', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px #0001', zIndex: 5001 }}>
            <FaTimes onClick={closeGottaGoal} style={{ position: 'absolute', top: 6, right: 6, fontSize: 12, cursor: 'pointer', color: '#888', zIndex: 10 }} />
            <h3 style={{ fontWeight: "bold" }}>Gotta goal?</h3>
            Set a first milestone or goal to aim for.
          </div>
        </Flex>
      )}

      <div ref={containerRef} style={{ visibility: loading ? "hidden" : "visible", paddingTop: 70 }}>
        {/* Filters for the grid */}
        <div style={{ position: "absolute", zIndex: 10, right: 20, top: 80 }}>
          <Flex style={{ alignItems: "center" }} gap={8}>
            <FilterButton text={viewMode === 'days' ? 'Days' : 'Weeks'} checked={viewMode !== 'days'} onClick={cycleViewMode} />
            <FilterButton text={groupBy === 'noGroup' ? 'Group' : 'Discipline'} checked={groupBy === 'discipline'} onClick={toggleGroupBy} />
            <FilterButton text={showPrivateGoals ? "My goals" : "Goals"} checked={showPrivateGoals} onClick={togglePrivateGoalsFilter} />
            <FilterButton text={showFullTeam ? "Full Team" : "Team"} checked={showFullTeam} onClick={toggleTeamFilter} />
            {/* <FilterButton text="Departed" checked={hideDeparted} onClick={() => setHideDeparted(!hideDeparted)} /> */}
          </Flex>
        </div>

        <FlexBottom ref={rowsAreaRef} gap={GRID_SPACE_WEEKEND}>
          <FlexLeftCol
            gap={GRID_SPACE_Y}
            // minWidth/minHeight keep the column (and the button row / Today
            // holder anchored above it) at full size even when the Team
            // filter leaves no visible rows.
            style={{ paddingBottom: 2, position: 'relative', minWidth: nameColumnWidth, minHeight: noVisibleRows ? emptyListHeight : MIN_ROWS_AREA_HEIGHT }}
          >
            {/* Left arrow, add-person plus and right arrow on one row spanning
                the full name-list width, anchored just above the name list */}
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "calc(100% + 8px)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 4px",
              }}
            >
              {/* Today jump button, centered just above the button row with the
                  same 8px gap the row has to the name list */}
              {!todayInRange && (
                <div style={{ position: "absolute", left: "50%", bottom: "calc(100% + 8px)", transform: "translateX(-50%)" }}>
                  <TodayButton onClickCb={scrollToToday} />
                </div>
              )}
              <CircleArrow onClickCb={() => scrollToDate(getCenterDate().subtractDays(30, true))} />
              <CirclePlus
                addGoalCb={() => {
                  editingGoal.current = { date: getWeekdayToday(), type: "minor" };
                  submitGoal(null, true);
                }}
                addPersonCb={() => {
                  addPersonDate.current = getWeekdayToday();
                  setShowAddPerson(true);
                }}
              />
              <CircleArrow inverted onClickCb={() => scrollToDate(getCenterDate().addDays(30, true))} />
            </div>
            {(() => {
              // Create flattened array matching dots order (shared logic)
              let peopleForList = peopleInView || [];
              let groupsInfo = [];
              
              if (groupBy === 'discipline' && peopleInView) {
                const grouped = {};
                const noDiscipline = [];
                
                peopleInView.forEach((peep) => {
                  const disciplineId = peep.person.discipline_id;
                  if (disciplineId) {
                    if (!grouped[disciplineId]) {
                      grouped[disciplineId] = [];
                    }
                    grouped[disciplineId].push(peep);
                  } else {
                    noDiscipline.push(peep);
                  }
                });

                const sortedDisciplines = disciplines
                  .filter(d => grouped[d.id] && grouped[d.id].length > 0)
                  .sort((a, b) => (a.order || 0) - (b.order || 0));

                // Create flattened array matching dots order
                peopleForList = [];
                sortedDisciplines.forEach((discipline) => {
                  groupsInfo.push({ discipline, people: grouped[discipline.id], startIdx: peopleForList.length });
                  peopleForList.push(...grouped[discipline.id]);
                });
                
                if (noDiscipline.length > 0) {
                  groupsInfo.push({ discipline: null, people: noDiscipline, startIdx: peopleForList.length });
                  peopleForList.push(...noDiscipline);
                }
              }

              if (groupBy === 'discipline' && groupsInfo.length > 0) {
                return (
                  <>
                    {groupsInfo.map((group, groupIdx) => {
                      const isFirstGroup = groupIdx === 0;
                      return (
                        <div key={"group-" + groupIdx} style={{ marginTop: !isFirstGroup ? 8 : 0 }}>
                          {group.discipline ? (
                            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                              {/* Colored vertical bar */}
                              <div
                                style={{
                                  width: 4,
                                  backgroundColor: group.discipline.color,
                                  marginRight: 0,
                                  alignSelf: 'stretch',
                                  minHeight: group.people.length * (BOX_SIZE + GRID_SPACE_Y) - GRID_SPACE_Y,
                                }}
                              />
                              {/* People in this discipline */}
                              <FlexLeftCol gap={GRID_SPACE_Y} style={{ flex: 1 }}>
                                {group.people.map((peep, i) => {
                                  const name = formatDisplayName(peep.person);
                                  const isMe = peep.person.id === user.id;
                                  return (
                                    <Row
                                      key={"person-" + peep.person.id + "-" + i}
                                      height={BOX_SIZE}
                                      width={nameColumnWidth}
                                      name={name}
                                      notInvited={peep.person.uid === null}
                                      isMe={isMe}
                                      grid_space_x={GRID_SPACE_X}
                                      canEdit={roleAccess("manager")}
                                      removePersonCb={() => removePerson(peep.person.id, peep.person.firstname + " " + peep.person.lastname)}
                                      showInviteUser={() => {
                                        invitingUser.current = peep.person;
                                        setShowInviteUser(true);
                                      }}
                                      personId={peep.person.id}
                                      isDotHovered={hoveredPersonId === peep.person.id}
                                    />
                                  );
                                })}
                              </FlexLeftCol>
                            </div>
                          ) : (
                            // No discipline group - wider width to compensate for missing bar
                            <FlexLeftCol gap={GRID_SPACE_Y}>
                              {group.people.map((peep, i) => {
                                const name = formatDisplayName(peep.person);
                                const isMe = peep.person.id === user.id;
                                return (
                                  <Row
                                    key={"person-no-discipline-" + peep.person.id + "-" + i}
                                    height={BOX_SIZE}
                                    width={nameColumnWidth}
                                    name={name}
                                    notInvited={peep.person.uid === null}
                                    isMe={isMe}
                                    grid_space_x={GRID_SPACE_X}
                                    canEdit={roleAccess("manager")}
                                    removePersonCb={() => removePerson(peep.person.id, peep.person.firstname + " " + peep.person.lastname)}
                                    showInviteUser={() => {
                                      invitingUser.current = peep.person;
                                      setShowInviteUser(true);
                                    }}
                                    personId={peep.person.id}
                                    isDotHovered={hoveredPersonId === peep.person.id}
                                  />
                                );
                              })}
                            </FlexLeftCol>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              } else {
                // No grouping - original rendering
                return (
                  <>
                    {peopleInView?.map((peep, i) => {
                      const name = formatDisplayName(peep.person);
                      const isMe = peep.person.id === user.id;
                      return (
                        <Row
                          key={"person" + i}
                          height={BOX_SIZE}
                          width={nameColumnWidth}
                          name={name}
                          notInvited={peep.person.uid === null}
                          isMe={isMe}
                          grid_space_x={GRID_SPACE_X}
                          canEdit={roleAccess("manager")}
                          removePersonCb={() => removePerson(peep.person.id, peep.person.firstname + " " + peep.person.lastname)}
                          showInviteUser={() => {
                            invitingUser.current = peep.person;
                            setShowInviteUser(true);
                          }}
                          personId={peep.person.id}
                          isDotHovered={hoveredPersonId === peep.person.id}
                        />
                      );
                    })}
                  </>
                );
              }
            })()}
          </FlexLeftCol>

          <div style={{ position: "relative", flex: 1, minWidth: 0, display: "flex", alignItems: "flex-end" }}>
          <HScroll ref={attachScrollerRef}>
          <div ref={dotsContainerRef} style={{ position: "relative", width: universeWidth, display: "inline-block" }}>
            {/* Everything above the grid of days incl goals, day dates and month names */}
            <DatesGoals
              dates={dates?.current}
              scrollerRef={scrollerRef}
              windowStart={windowRange.start}
              windowEnd={windowRange.end}
              goals={goals?.current?.filter((goal) => {
                if (goal.type === "unplanned") return false;
                if (!showPrivateGoals && goal.private) return false;
                // Weeks view is zoomed out: only show major milestones (and the
                // TODAY marker). Days view shows all flags again.
                if (viewMode === "weeks" && goal.type !== "major" && goal.type !== "today") return false;
                return true;
              })}
              size={BOX_SIZE}
              space_x={GRID_SPACE_X}
              space_y={GRID_SPACE_Y}
              animationDone={tl.current?.totalProgress() === 1}
              goalPopupOpen={showEditGoal}
              addGoalCb={(date) => {
                editingGoal.current = { date: date, type: "minor" };
                submitGoal(null, true);
              }}
              addPersonCb={(date) => {
                addPersonDate.current = date;
                setShowAddPerson(true);
              }}
              editGoalCb={(id, newIndex) => {
                if (typeof newIndex === "number") {
                  // Moved goal
                  submitGoal({ id: id, date: dates.current[newIndex].date.toISOString() });
                } else {
                  const goal = goals.current.find((g) => g.id === id);
                  editingGoal.current = goal;
                  setShowEditGoal(true);
                }
              }}
              showParentCb={() => {
                setLoading(false);
              }}
              hideAddButton={movingDay.current}
              viewMode={viewMode}
            />

            {/* Main loop displaying the grid of days */}
            {(() => {
              // Create flattened array matching visual order for grouped rendering
              let peopleForDots = peopleInView || [];
              let personYOffsets = {};
              let totalHeight = peopleInView?.length * (BOX_SIZE + GRID_SPACE_Y) || MIN_ROWS_AREA_HEIGHT;

              if (groupBy === 'discipline' && peopleInView) {
                const grouped = {};
                const noDiscipline = [];
                
                peopleInView.forEach((peep) => {
                  const disciplineId = peep.person.discipline_id;
                  if (disciplineId) {
                    if (!grouped[disciplineId]) {
                      grouped[disciplineId] = [];
                    }
                    grouped[disciplineId].push(peep);
                  } else {
                    noDiscipline.push(peep);
                  }
                });

                const sortedDisciplines = disciplines
                  .filter(d => grouped[d.id] && grouped[d.id].length > 0)
                  .sort((a, b) => (a.order || 0) - (b.order || 0));

                // Create flattened array in grouped visual order and calculate Y offsets
                peopleForDots = [];
                let currentY = 0;
                
                sortedDisciplines.forEach((discipline, groupIdx) => {
                  if (groupIdx > 0) {
                    currentY += 8; // Padding between groups
                  }
                  grouped[discipline.id].forEach((peep) => {
                    const visualIndex = peopleForDots.length;
                    peopleForDots.push(peep);
                    personYOffsets[visualIndex] = currentY;
                    currentY += BOX_SIZE + GRID_SPACE_Y;
                  });
                });
                
                if (noDiscipline.length > 0 && sortedDisciplines.length > 0) {
                  currentY += 8; // Padding before no-discipline group
                }
                noDiscipline.forEach((peep) => {
                  const visualIndex = peopleForDots.length;
                  peopleForDots.push(peep);
                  personYOffsets[visualIndex] = currentY;
                  currentY += BOX_SIZE + GRID_SPACE_Y;
                });

                // Calculate total height using the actual currentY value which already includes all padding
                totalHeight = currentY;
              } else {
                // No grouping - simple linear offsets
                peopleForDots.forEach((peep, idx) => {
                  personYOffsets[idx] = idx * (BOX_SIZE + GRID_SPACE_Y);
                });
              }

              // When the Team filter leaves no visible rows, keep the grid at
              // the height the full list would occupy. Nothing renders in it,
              // so it acts as an invisible catcher that keeps horizontal
              // scroll gestures working over the (empty) dots area.
              if (peopleForDots.length === 0) {
                totalHeight = emptyListHeight;
              } else {
                // Few visible rows: keep the same minimum as the empty state
                // so horizontal scroll gestures still land on the grid. The
                // extra space sits below the rows (the name column reserves
                // the same minimum, so rows stay aligned top-to-top).
                totalHeight = Math.max(totalHeight, MIN_ROWS_AREA_HEIGHT);
              }

              peopleForDotsRef.current = peopleForDots;
              personYOffsetsRef.current = personYOffsets;

              return (
                <div ref={dotsGridRef} style={{ position: "relative", height: totalHeight }}>
                  {peopleForDots.map((peep, k) => {
                    const isMe = user.id === peep.person.id;
                    const yOffset = personYOffsets[k] || (k * (BOX_SIZE + GRID_SPACE_Y));

                    // Windowed virtualization: only mount dots whose slot is in
                    // the visible window (+buffer). dayIndex is the absolute
                    // universe slot index, so positions/selection stay stable.
                    return peep.days.slice(windowRange.start, windowRange.end + 1).map((day) => {
                      if (!day.isActive) return null;
                      const selected = selectedDays?.current?.find((d) => d.row === k && (d.stamp != null ? d.stamp === day.daystamp : d.col === day.dayIndex));
                      const canShowPopup = selectedDays.current ? selectedDays.current.length <= 1 : true;
                      return (
                        <Dot
                          key={"day-" + k + "-" + day.dayIndex}
                          data={{ ...day, yOffset, visualRowIndex: k }}
                          row={k}
                      size={BOX_SIZE}
                      space_y={GRID_SPACE_Y}
                      callback={selectDay}
                      selected={selected}
                      canShowPopup={canShowPopup && !day.isFirstOrLast}
                      isFirstOrLast={day.isFirstOrLast}
                      dates={dates?.current}
                      startDragJoinLeaveDayCb={(date, xOffset) => startDragJoinLeaveDay(peep.person.id, date, xOffset)}
                      canEdit={roleAccess("lead") || (isMe && day.day >= new Date())}
                    />
                  );
                });
              })}
                </div>
              );
            })()}
          </div>
          </HScroll>

          {/* Transparent edge overlays: block the outermost day dots and scroll
              the timeline a month on click. */}
          <EdgeArrow height={gridHeight} onClickCb={() => scrollToDate(getCenterDate().subtractDays(30, true))} />
          <EdgeArrow inverted height={gridHeight} onClickCb={() => scrollToDate(getCenterDate().addDays(30, true))} />
          </div>
        </FlexBottom>
      </div>
    </div>
  );
}

